import React, { useEffect, useRef, useState, useCallback } from 'react';

import type { FlowEngine } from '../../services/flows';
import type { Step } from '../../services/flows/types';
import { useNumberFormatter } from '../../utils/formatters';

interface TextValidation {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternError?: string;
}

interface TextInputStepType extends Step {
  type: 'textInput';
  prompt: string;
  placeholder?: string;
  required?: boolean;
  validation?: TextValidation;
}

interface TextInputStepProps {
  step: TextInputStepType;
  engine: FlowEngine;
  onMessage: (message: { from: string; text: string }) => void;
}

const TextInputStep: React.FC<TextInputStepProps> = ({ step, engine, onMessage }) => {
  const [isWaitingForInput, setIsWaitingForInput] = useState(false);
  const [, setValidationError] = useState<string | null>(null);
  const hasShownPromptRef = useRef(false);
  const { formatNumber } = useNumberFormatter();

  const requestInput = useCallback(() => {
    setIsWaitingForInput(true);
    setValidationError(null);

    // Emit event to signal input is needed
    engine.emit('inputRequested', {
      type: 'text',
      config: {
        required: step.required !== false, // Default to true
        placeholder: step.placeholder ? __(step.placeholder) : __('Type your response...'),
        validation: step.validation,
      },
      step: step,
    });
  }, [engine, step]);

  // Show prompt on mount
  useEffect(() => {
    if (!hasShownPromptRef.current && step.prompt) {
      onMessage({ from: 'bot', text: __(step.prompt) });
      hasShownPromptRef.current = true;
      requestInput();
    }
  }, [step.prompt, onMessage, requestInput]);

  const validateTextInput = useCallback((value: string): string | null => {
    const validation = step.validation;
    if (!validation) return null;

    if (validation.minLength && value.length < validation.minLength) {
      return `${__('Please enter at least')} ${formatNumber(validation.minLength)} ${__('characters')}`;
    }

    if (validation.maxLength && value.length > validation.maxLength) {
      return `${__('Please keep your response under')} ${formatNumber(validation.maxLength)} ${__('characters')}`;
    }

    if (validation.pattern) {
      const regex = new RegExp(validation.pattern);
      if (!regex.test(value)) {
        return validation.patternError || __('Invalid format');
      }
    }

    return null;
  }, [step.validation, formatNumber]);

  // Listen for input completion
  useEffect(() => {
    const handleInputComplete = (event: any) => {
      if (!isWaitingForInput || event.type !== 'text') return;

      const value = event.value;

      // Handle skip for optional fields
      if (!value && step.required === false) {
        setIsWaitingForInput(false);
        engine.goToNext(step.next || '');
        return;
      }

      // Validate required fields
      if (!value && step.required !== false) {
        setValidationError(__('This field is required'));
        onMessage({ from: 'bot', text: __('This field is required') });
        requestInput(); // Request again
        return;
      }

      // Validate input
      const error = validateTextInput(value);
      if (error) {
        setValidationError(error);
        onMessage({ from: 'bot', text: error });
        requestInput(); // Request again
        return;
      }

      // Success - update variable and navigate
      setIsWaitingForInput(false);
      engine.updateBuiltInVariable('lastUserText', value);

      // Store in specified variable if storeAs is provided
      if (step.storeAs) {
        engine.setVariable(step.storeAs, value);
      }

      // Add user message to chat
      onMessage({ from: 'user', text: value });

      // Navigate to next step
      if (step.next) {
        engine.goToNext(step.next);
      }
    };

    engine.on('inputComplete', handleInputComplete);

    return () => {
      engine.off('inputComplete', handleInputComplete);
    };
  }, [
    isWaitingForInput,
    step.required,
    step.next,
    step.storeAs,
    engine,
    onMessage,
    requestInput,
    validateTextInput,
  ]);

  // Component is purely orchestration - no visual elements
  return null;
};

export default TextInputStep;